import { Button, useToastContext, Text, Spinner } from "@ttc3k/trekker";
import { AnimatePresence } from "framer-motion";
import {
  EnumBookerStatus,
  EnumProductType,
  useBookerManyQuery,
} from "gql/generated";
import { PlusCircle, ArrowRight } from "iconoir-react";
import { useState } from "react";
import { useWatch, FormProvider } from "react-hook-form";
import { AnimatedPage, EmptyState, WhereaboutsModal } from "shared/components";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { useManualOrderForm } from "./useManualOrderForm";
import { ManualOrderFormComponents } from "./ManualOrderFormComponents";
import { ManualOrderReview } from "./ManualOrderReview";
import { useTranslation } from "react-i18next";

type CreateManualOrderModalProps = {
  isOpen: boolean;
  onClose: (reloadOrders?: boolean) => void;
  storefrontId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export const CreateManualOrderModal = ({
  isOpen,
  onClose,
  storefrontId,
  firstName,
  lastName,
  email,
}: CreateManualOrderModalProps) => {
  const [formError, setFormError] = useState("");
  const [screen, setScreen] = useState<"build" | "review">("build");

  const { toastFactory } = useToastContext();
  const { t } = useTranslation("storefront", {
    keyPrefix: "CALENDAR.MANUAL_ORDER",
  });

  const { data, loading, error } = useBookerManyQuery({
    variables: {
      filter: { storefront: storefrontId, status: [EnumBookerStatus.Open] },
    },
  });

  const { form, onSubmit, defaultValues } = useManualOrderForm({
    screen,
    onSuccess: orderId => {
      form.reset(defaultValues);
      setScreen("build");
      setFormError("");
      onClose(true);
      toastFactory.create({
        title: t("SUCCESS.TITLE"),
        description: t("SUCCESS.DESCRIPTION", { orderId }),
      });
    },
    onError: m => setFormError(m),
    storefrontId,
    bookers: data?.bookerMany ?? [],
    firstName,
    lastName,
    email,
  });

  const formId = "booker-manual-order-form";

  const bookerOptions =
    (data?.bookerMany ?? []).map(b => ({
      label:
        b.name && getMultilingualString(b.name).length > 0
          ? getMultilingualString(b.name)
          : b.bundles[0]?.product?.name &&
              getMultilingualString(b.bundles[0].product.name).length > 0
            ? getMultilingualString(b.bundles[0].product.name)
            : t("NO_BOOKER_NAME"),
      value: b._id as string,
      type: b.bundles[0]?.product?.type ?? EnumProductType.Fee,
    })) ?? [];

  const bookerId = useWatch({
    control: form.control,
    name: "bookerId",
  });

  const handleClose = () => {
    form.reset(defaultValues);
    setScreen("build");
    onClose();
  };

  const handleReviewClick = async () => {
    const isValid = await form.trigger();

    if (isValid) {
      // Bug with react-hook-forms, when screen state changes at the same time
      // as the form is triggered, it triggers the submission of the form which
      // we don't want
      setTimeout(() => setScreen("review"), 1);
    }
  };

  const getCurrentScreen = (scrn: "build" | "review") => {
    if (error) {
      return <Text>{t("ERRORS.LOADING_BOOKERS")}</Text>;
    }
    if (!data?.bookerMany || data.bookerMany.length === 0) {
      return <EmptyState text={t("EMPTY_STATE")} />;
    }
    switch (scrn) {
      case "build":
        return (
          <ManualOrderFormComponents
            bookerOptions={bookerOptions}
            allBookers={data?.bookerMany ?? []}
            isForSpecificPerson={firstName && lastName && email ? true : false}
          />
        );
      case "review":
        return <ManualOrderReview allBookers={data?.bookerMany ?? []} />;
    }
  };
  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("TITLE")}
      description={t("DESCRIPTION")}
      HeaderIcon={PlusCircle}
      cancelButtonProps={{ onClick: handleClose }}
      customFooter={
        <Flex
          px={"500"}
          py={"400"}
          flex={screen === "build" ? 1 : undefined}
          gap={"150"}
          justifyContent={"space-between"}
        >
          <Button
            flex={screen === "build" ? 1 : undefined}
            size={"lg"}
            type={"button"}
            mode={"light"}
            onClick={handleClose}
          >
            {t("CANCEL")}
          </Button>
          <Flex flex={screen === "build" ? 1 : undefined} gap={"100"}>
            {screen === "review" && (
              <Button
                size={"lg"}
                mode={"light"}
                onClick={() => setScreen("build")}
              >
                {t("BACK")}
              </Button>
            )}
            <Button
              flex={screen === "build" ? 1 : undefined}
              size={"lg"}
              form={screen === "review" ? formId : undefined}
              type={screen === "review" ? "submit" : "button"}
              disabled={bookerId.length < 1}
              isLoading={form.formState.isSubmitting}
              Icon={screen === "build" ? ArrowRight : undefined}
              isIconRightAligned={screen === "build" ? true : undefined}
              onClick={screen === "build" ? handleReviewClick : undefined}
            >
              {screen === "build" ? t("REVIEW_BUTTON") : t("SEND_INVOICE")}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <FormProvider {...form}>
        <form onSubmit={onSubmit} id={formId}>
          {loading ? (
            <Spinner width={"50px"} height={"50px"} margin={"60px auto"} />
          ) : (
            <AnimatePresence mode={"wait"}>
              <AnimatedPage
                key={screen}
                animationProps={{
                  transition: {
                    duration: 0.75,
                    ease: "easeInOut",
                    type: "spring",
                  },
                }}
                initial={screen === "build" ? "exit" : "enter"}
                exit={screen === "build" ? "exit" : "enter"}
              >
                <Flex flexDirection={"column"} gap={"200"}>
                  {getCurrentScreen(screen)}
                </Flex>
              </AnimatedPage>
            </AnimatePresence>
          )}
        </form>
      </FormProvider>
      {formError.length > 0 && (
        <Text visual={"smallSemiBold"} color={"text.error.dark"} mt={"100"}>
          {formError}
        </Text>
      )}
    </WhereaboutsModal>
  );
};
